import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import { Reveal } from "../components/Reveal";
import WordReveal from "../components/WordReveal";
import BrandText from "../components/BrandText";
import FeatureStrip from "../components/FeatureStrip";
import { Team, ComplianceSection, HeritageStrip } from "../components/CompanySections";

const U = (id, w = 1600) => `/assets/interiors/${id}-${w <= 320 ? 320 : w <= 640 ? 640 : w <= 1000 ? 1000 : 1600}.webp`;

const MAKERS = [
  ["sh-98702", "Champagne Star Lantern Trees"],
  ["sh-98742", "Grand Antler Reindeer"],
  ["sh-98719", "Carved Tree on Dark Base"],
  ["sh-98729", "Mercury Glass Tree Pair"],
  ["sh-98766", "Star Wreath Candle Holder"],
  ["sh-98782", "Engraved Heart Board"],
  ["sh-98785", "Green Resin Christmas Tree Board"],
  ["sh-98779", "Stag-Handled Wooden Bowl"],
];

const LIST_ITEM = "flex gap-5 py-4 border-b border-line text-[14px] text-charcoal";

export default function About() {
  const [content, setContent] = useState(null);
  useEffect(() => { api.site("content").then((data) => setContent((data.pages || []).find((page) => page.slug === "about") || null)).catch(() => {}); }, []);
  const title = content?.title || "Crafted in Moradabad. Considered for the world.";
  const paragraphs = content?.sections?.map((section) => section.body) || [
    "S H & SONS ® is a home décor manufacturer and exporter based in Moradabad, India. Our collections bring together metal, glass, wood and marble for international wholesale and retail markets.",
    "We work with importers, wholesalers and retail brands outside India. This showroom lets buyers explore our product range, build a shortlist and discuss bulk requirements directly with our export team.",
    "Share your preferred materials, finishes, quantities and target market. Together we can discuss the product brief, sample requirements, production details and packing for your collection.",
  ];
  return (
    <div data-testid="about-page" className="pb-24">
      <SEO
        title="About — S H & SONS ®"
        description="Home décor manufacturer and exporter in Moradabad, India since 1986. 1,500+ artisans and an 81,675 sq ft factory support wholesale, OEM and custom collections."
      />

      <Team />

      <section className="h-[38vh] min-h-[250px] max-h-[520px] overflow-hidden bg-beige">
        <img src={U("1616486338812-3dadae4b4ace", 2200)} alt="Contemporary interior inspiration" className="w-full h-full object-cover" />
      </section>

      <section className="mx-auto max-w-[1000px] px-6 py-24 lg:py-32 text-center">
        <p className="eyebrow mb-6"><BrandText text={content?.intro || "Why we exist"} /></p>
        <WordReveal as="h1" text={title} className="h-display text-4xl sm:text-5xl lg:text-[64px] text-balance" />
        <div className="mt-14 space-y-6 text-[14px] sm:text-[15px] text-charcoal leading-[1.85] max-w-2xl mx-auto">
          {paragraphs.map((paragraph, index) => <p key={index}><BrandText text={paragraph} /></p>)}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 sm:px-8 py-16" data-testid="makers-gallery">
        <div className="mb-14 grid gap-8 lg:grid-cols-[1.05fr_.95fr] lg:items-end lg:gap-16">
          <div>
            <p className="eyebrow mb-4">From our Christmas Collection 2027 · Made in Moradabad</p>
            <WordReveal text="Four materials. One considered point of view." className="h-display max-w-3xl text-4xl sm:text-5xl lg:text-[62px] leading-[1.02]" />
            <p className="mt-6 max-w-xl text-sm sm:text-[15px] leading-[1.85] text-mocha">Explore a buyer-ready mix of metal, glass, carved wood and marble home décor. Each material brings its own finish and character; together they create a collection your customers can style across the home.</p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {[["01", "Metal", "Sculptural accents & warm finishes"], ["02", "Glass", "Reflective detail for festive spaces"], ["03", "Wood", "Natural grain & hand-carved texture"], ["04", "Marble", "Grounded forms with quiet contrast"]].map(([number, material, detail]) => <div key={material} className="min-h-[112px] rounded-2xl border border-line bg-white/65 p-4 sm:p-5"><span className="text-[10px] tracking-[.16em] text-brass">{number} / MATERIAL</span><h3 className="mt-3 h-display text-2xl sm:text-3xl">{material}</h3><p className="mt-1 text-[11px] sm:text-xs leading-relaxed text-mocha">{detail}</p></div>)}
          </div>
        </div>
        <div className="columns-2 lg:columns-4 gap-4 sm:gap-6 [&>*]:mb-4 sm:[&>*]:mb-6">
          {MAKERS.map(([code, name], i) => (
            <Reveal key={code} delay={(i % 4) * 0.06}>
              <Link to={`/product/${code}`} aria-label={`View ${name}`} className="block rounded-[22px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brass">
                <div className={`overflow-hidden rounded-[22px] bg-beige ${i % 3 === 0 ? "aspect-[3/4]" : i % 3 === 1 ? "aspect-square" : "aspect-[4/5]"}`}>
                  <img src={`/assets/catalogue/christmas-2027/${code}-card.webp`} alt={`${name} — ${code.toUpperCase()}`} loading="lazy" decoding="async" className="w-full h-full object-contain p-5 sm:p-7" />
                </div>
                <p className="mt-3 text-sm text-charcoal">{name}</p>
                <p className="mt-1 text-xs text-mocha">{code.toUpperCase()}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="craftsmanship" className="bg-[#161616] text-white" data-testid="craftsmanship-section">
        <div className="mx-auto max-w-[1300px] px-6 sm:px-10 py-24 lg:py-32 grid lg:grid-cols-2 gap-14 lg:gap-24 items-center">
          <div>
            <img src="/assets/shsons-emblem-cream.svg" alt="S H & SONS ® — official Tree Emblem, Cream Reverse" width="667" height="700" loading="lazy" className="h-24 w-auto object-contain mb-10" />
            <p className="eyebrow text-white/50 mb-5">Made of mettle</p>
            <WordReveal dark text="S H & SONS ®, Moradabad" className="h-display text-4xl sm:text-5xl lg:text-[56px]" />
            <p className="mt-8 text-[14px] sm:text-[15px] text-white/70 leading-[1.85] max-w-lg">
              From a shortlist of designs to a sourcing brief, our team helps buyers discuss the possibilities across metal, glass, wood and marble. Each enquiry starts with your market, specification and quantity requirements.
            </p>
            <ul className="mt-10 space-y-0 text-white">
              {[
                ["0.1", "Product selection for wholesale collections"],
                ["0.2", "Materials and finishes for your brief"],
                ["0.3", "Sampling and specification discussions"],
                ["0.4", "Packing and dispatch requirements"],
              ].map(([n, t]) => (
                <li key={n} className={`${LIST_ITEM} border-white/10 text-white/85`} data-testid={`mettle-${n.replace(".", "-")}`}>
                  <span className="text-white/40 text-[12px] pt-0.5">{n}</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <Reveal>
            <div className="overflow-hidden rounded-[28px]">
              <img src={U("1616486338812-3dadae4b4ace", 1400)} alt="Material and workshop inspiration" className="w-full aspect-[4/5] object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1300px] px-6 sm:px-10 py-24 lg:py-32 grid lg:grid-cols-2 gap-14 lg:gap-24 items-center" data-testid="finding-section">
        <div>
          <p className="eyebrow mb-5">Finding <span className="brand-name">S H &amp; SONS ®</span></p>
          <WordReveal text="Begin with a conversation" className="h-display text-4xl sm:text-5xl lg:text-[56px]" />
          <p className="mt-8 text-[14px] sm:text-[15px] text-charcoal leading-[1.85] max-w-lg">
            Our export team is based in Moradabad, India. We welcome bulk sourcing enquiries from international buyers. Email your shortlist or tell us what you would like to develop.
          </p>
          <ul className="mt-10">
            {[
              ["0.1", "www.shandsons.in — our international showroom"],
              ["0.2", "hello@shandsons.in — our export team"],
              ["0.3", "Moradabad, India — manufacturing and export"],
              ["0.4", "+91 9870651826 — contact our team"],
            ].map(([n, t]) => (
              <li key={n} className={LIST_ITEM}>
                <span className="text-mocha text-[12px] pt-0.5">{n}</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <Link to="/contact" data-testid="about-contact-cta" className="pill-dark mt-10 px-8">Get in touch</Link>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:gap-6">
          <Reveal>
            <div className="overflow-hidden rounded-[22px] bg-beige aspect-[3/4] mt-10">
              <img src={U("1600210492486-724fe5c67fb0", 900)} alt="Living room inspiration" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="overflow-hidden rounded-[22px] bg-beige aspect-[3/4]">
              <img src={U("1618220179428-22790b461013", 900)} alt="Bedroom styling inspiration" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <HeritageStrip />
      <ComplianceSection />
      <FeatureStrip />

      <section className="mx-auto max-w-3xl px-6 py-16 text-center">
        <WordReveal text="Put Indian making on the world's mantelpiece — one considered object at a time." className="h-display text-2xl sm:text-4xl lg:text-[40px] leading-[1.25]" />
        <Link to="/shop" data-testid="about-shop-cta" className="pill-dark mt-10 px-8">Explore the Collection</Link>
      </section>
    </div>
  );
}
